import { useEffect } from "react";

export default function App() {
  // Calculate duration from start date to now
  const calculateDuration = (startDate) => {
    const start = new Date(startDate);
    const now = new Date();
    const diffInMonths = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
    
    if (diffInMonths < 12) {
      return `${diffInMonths} mois`;
    } else {
      const years = Math.floor(diffInMonths / 12);
      const months = diffInMonths % 12;
      if (months === 0) {
        return `${years} an${years > 1 ? 's' : ''}`;
      } else {
        return `${years} an${years > 1 ? 's' : ''} ${months} mois`;
      }
    }
  };

  // --- Runtime smoke tests (executed after first render) ---
  useEffect(() => {
    try {
      const get = (sel) => document.querySelector(sel);
      const tests = [
        { test: "Root wrapper exists", pass: !!get("div.font-sans") },
        { test: "Hero section exists", pass: !!get("#hero") },
        { test: "Résumé section exists", pass: !!get("#about") },
        { test: "Compétences section exists", pass: !!get("#skills") },
        { test: "Expérience section exists", pass: !!get("#experience") },
        { test: "Éducation section exists", pass: !!get("#education") },
        { test: "Contact section exists", pass: !!get("#contact") },
      ];
      // eslint-disable-next-line no-console
      console.table(tests);
    } catch {
      /* no-op */
    }
  }, []);

  return (
    <div className="font-sans text-gray-800 bg-white">
      {/* Hero Section */}
      <header
        id="hero"
        className="min-h-screen flex flex-col justify-center items-center text-center px-6 bg-gradient-to-br from-blue-50 to-white"
      >
        <div className="mb-6">
          <img 
            src="/2023.webp" 
            alt="Julien Larocque-Dupont" 
            className="rounded-full mx-auto mb-6 shadow-lg object-cover"
            style={{
              width: '346px',
              height: '346px'
            }}
          />
        </div>
        <h1 className="text-4xl md:text-6xl font-bold mb-2">Julien Larocque-Dupont, M.Sc.</h1>
        <p className="text-lg md:text-2xl text-gray-600 mb-6">
          Architecte de solutions et de plateformes dans le domaine financier
        </p>
        <p className="max-w-2xl text-gray-500 mb-8">
          Architecte passionné par la finance, la technologie et le monde des données.
          <br />
          Expert en conception et implémentation de solutions technologiques robustes alignées aux besoins d'affaires depuis plus de 20 ans.
        </p>
        <div className="flex gap-4 flex-wrap justify-center">
          <a
            href="/Curriculum_vitae_Julien_Larocque-Dupont.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 bg-blue-600 text-white rounded-2xl shadow hover:bg-blue-700 transition hidden"
          >
            Télécharger CV
          </a>
          <a
            href="https://www.linkedin.com/in/julienld"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 border border-blue-600 text-blue-600 rounded-2xl hover:bg-blue-50 transition"
          >
            LinkedIn
          </a>
          <a
            href="tel:514-883-0530"
            className="px-6 py-3 bg-gray-600 text-white rounded-2xl shadow hover:bg-gray-700 transition"
          >
            514-883-0530
          </a>
        </div>
      </header>

      <main>
        {/* Résumé */}
        <section id="about" className="py-20 px-6 max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold mb-6 text-center">En bref</h2>
          <div className="text-gray-600 leading-relaxed space-y-4 text-left">
            <p>
              Architecte passionné par la finance, les technologies, le DevOps et le monde des données. 
              Détenteur d'une maîtrise en Technologie de l'information de l'École des sciences de la gestion, 
              je comprends les enjeux organisationnels et technologiques à l'échelle de l'entreprise.
            </p>
            <p>
              Durant mes études, je me suis particulièrement intéressé au travail collaboratif et aux pratiques 
              permettant de faire de l'architecture en contexte Agile. Mon parcours professionnel m'a mené à me spécialiser dans :
            </p>
            <div className="text-left max-w-2xl space-y-3">
              <div className="flex items-start">
                <span className="text-blue-600 mr-1">★</span>
                <span><strong>La finance de marché :</strong> conception et développement de solutions innovantes pour optimiser et automatiser des transactions financières.</span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-600 mr-1">★</span>
                <span><strong>Les données et l'analytique :</strong> utilisation de concepts d'architecture pour gérer la complexité de solutions existantes et assurer leur pérennité tout en augmentant leur capacité d'évoluer rapidement.</span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-600 mr-1">★</span>
                <span><strong>La construction de solutions logicielles :</strong> maîtrise des principes et pratiques DevOps pour livrer fréquemment des produits de qualité, adaptés aux besoins des utilisateurs.</span>
              </div>
            </div>
          </div>
        </section>

        {/* Domaines d'expertise - TEMPORAIREMENT CACHÉE */}
        <section id="skills" className="py-20 px-6 bg-gray-50 hidden">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-10 text-center">Domaines d'expertise</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-6 bg-white rounded-2xl shadow hover:shadow-lg transition">
                <div className="text-blue-600 text-3xl mb-4">🏗️</div>
                <h3 className="text-lg font-semibold mb-3">Gestion des technologies et architecture d'entreprise</h3>
                <ul className="text-gray-600 space-y-1 text-sm">
                  <li>Gouvernance des TI</li>
                  <li>Alignement organisationnel</li>
                  <li>Architecture de référence</li>
                  <li>Patrons de conception</li>
                </ul>
              </div>
              
              <div className="p-6 bg-white rounded-2xl shadow hover:shadow-lg transition">
                <div className="text-blue-600 text-3xl mb-4">⚙️</div>
                <h3 className="text-lg font-semibold mb-3">Architecture technologique</h3>
                <ul className="text-gray-600 space-y-1 text-sm">
                  <li>Amazon Web Services</li>
                  <li>Développement de plateforme</li>
                  <li>Desktop Virtualization</li>
                  <li>Plateforme en tant que service</li>
                </ul>
              </div>
              
              <div className="p-6 bg-white rounded-2xl shadow hover:shadow-lg transition">
                <div className="text-blue-600 text-3xl mb-4">💡</div>
                <h3 className="text-lg font-semibold mb-3">Architecture de solutions</h3>
                <ul className="text-gray-600 space-y-1 text-sm">
                  <li>Pratiques Agile</li>
                  <li>Expérience développeur</li>
                  <li>Solutions innovantes</li>
                  <li>Travail collaboratif</li>
                </ul>
              </div>
              
              <div className="p-6 bg-white rounded-2xl shadow hover:shadow-lg transition">
                <div className="text-blue-600 text-3xl mb-4">📊</div>
                <h3 className="text-lg font-semibold mb-3">Finance de marché</h3>
                <ul className="text-gray-600 space-y-1 text-sm">
                  <li>Business intelligence</li>
                  <li>Gestion de portefeuille</li>
                  <li>Science des données</li>
                  <li>Aide à la décision</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Expérience */}
        <section id="experience" className="py-20 px-6 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-10 text-center">Expérience</h2>
            <div className="space-y-8">
            <div className="border-l-4 border-blue-600 pl-6">
              <h3 className="text-xl font-semibold">La Caisse de dépôt et placement du Québec</h3>
              <p className="text-gray-500 italic mb-2">{calculateDuration('2014-05-01')}</p>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-700">Architecte de plateformes, Ingénierie des services</h4>
                  <p className="text-gray-500 italic text-sm">octobre 2023 - Présent ({calculateDuration('2023-10-01')})</p>
                  <p className="text-gray-600 mt-2">
                    Collaboration avec les experts techniques, les chargés de produits et les utilisateurs pour concevoir et implémenter 
                    des solutions innovantes et performantes. Organisation de l'utilisation des plateformes technologiques pour faciliter 
                    la vie des constructeurs de solutions de La Caisse.
                  </p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-700">Architecte de solutions</h4>
                  <p className="text-gray-500 italic text-sm">février 2021 - novembre 2023 (2 ans 10 mois)</p>
                  <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
                    <li>2021 : Équipe Investissement - Marchés liquides. Gestion de la connaissance en investissement</li>
                    <li>2022 : Équipe Données et analytique</li>
                    <li>2023 : Équipe Données et analytique / Stratégies quantitatives et science des données</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-700">Chargé de produit</h4>
                  <p className="text-gray-500 italic text-sm">août 2021 - août 2022 (1 an 1 mois)</p>
                  <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
                    <li>Équipe Données et analytique - Aide à la décision</li>
                    <li>Apprentissage machine</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-700">Analyste d'affaires principal</h4>
                  <p className="text-gray-500 italic text-sm">janvier 2019 - août 2021 (2 ans 8 mois)</p>
                  <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
                    <li>Négociation et gestion de portefeuille</li>
                    <li>Marchés boursiers et obligataires</li>
                    <li>Gestion de la connaissance en investissement</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-700">Analyste d'affaires</h4>
                  <p className="text-gray-500 italic text-sm">mai 2014 - décembre 2018 (4 ans 8 mois)</p>
                  <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
                    <li>Négociation et gestion de portefeuille</li>
                    <li>Prêt-emprunt de titre (Security lending)</li>
                    <li>Conformité des investissements et affaires juridiques</li>
                    <li>Investissement durable (ESG)</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="border-l-4 border-gray-400 pl-6">
              <h3 className="text-xl font-semibold">Banque Nationale</h3>
              <p className="text-gray-500 italic">Conseiller technique | 2011 - 2012 (1 an)</p>
              <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
                <li>Équipe Gestion de l'information et systèmes RH</li>
              </ul>
            </div>
            
            <div className="border-l-4 border-gray-400 pl-6">
              <h3 className="text-xl font-semibold">Québec-Hébergement</h3>
              <p className="text-gray-500 italic">Administrateur système et intégrateur web | 2005 - 2011 (6 ans)</p>
              <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
                <li>Administration de serveurs Linux Debian</li>
                <li>Conception de sites web</li>
              </ul>
            </div>
            
            <div className="border-l-4 border-gray-400 pl-6">
              <h3 className="text-xl font-semibold">Consultant en Technologies de l'information</h3>
              <p className="text-gray-500 italic">2005 - 2011 (6 ans)</p>
              <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
                <li>Service-conseil et digitalisation de processus d'affaires</li>
                <li>Mise en place de systèmes d'informations (ERP, CMS) pour des PMEs</li>
              </ul>
            </div>
          </div>
          </div>
        </section>

        {/* Formation */}
        <section id="education" className="py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-10 text-center">Formation</h2>
            <div className="space-y-8">
              <div className="border-l-4 border-blue-600 pl-6">
                <h3 className="text-xl font-semibold">Université du Québec à Montréal</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-700">Maîtrise ès sciences, Technologies de l'information</h4>
                    <p className="text-gray-500 italic text-sm">2013 - mai 2022</p>
                    <p className="text-gray-600 mt-2">
                      Spécialisation en architecture technologique, travail collaboratif et pratiques d'architecture en contexte Agile. 
                      Focus sur les enjeux organisationnels et technologiques à l'échelle de l'entreprise.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-700">Certificat, Administration</h4>
                    <p className="text-gray-500 italic text-sm">2011 - 2012</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-700">Majeure, Communication</h4>
                    <p className="text-gray-500 italic text-sm">2008 - 2010</p>
                  </div>
                </div>
              </div>
              
              <div className="border-l-4 border-gray-400 pl-6">
                <h3 className="text-xl font-semibold">Université de Montréal</h3>
                <div>
                  <h4 className="font-semibold text-gray-700">Cours en Mathématique-Informatique</h4>
                  <p className="text-gray-500 italic text-sm">2006 - 2008</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-20 px-6 bg-gray-50 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Contact</h2>
          <p className="text-gray-600 mb-8">
            Vous souhaitez discuter d'une collaboration ou en savoir plus?
          </p>
          
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="flex flex-col items-center p-4 bg-blue-50 rounded-2xl">
              <span className="text-blue-600 font-semibold">Courriel</span>
              <span className="text-gray-600 text-sm">julien@larocque-dupont.com</span>
            </div>
            
            <a
              href="tel:514-883-0530"
              className="flex flex-col items-center p-4 bg-blue-50 rounded-2xl hover:bg-blue-100 transition"
            >
              <span className="text-blue-600 font-semibold">Cellulaire</span>
              <span className="text-gray-600 text-sm">514-883-0530</span>
            </a>
            
            <a
              href="https://www.linkedin.com/in/julienld"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 bg-blue-50 rounded-2xl hover:bg-blue-100 transition"
            >
              <span className="text-blue-600 font-semibold">LinkedIn</span>
              <span className="text-gray-600 text-sm">julienld</span>
            </a>
          </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-gray-500 text-sm border-t">
        <p>© {new Date().getFullYear()} Julien Larocque-Dupont. Tous droits réservés.</p>
        <p className="mt-2 text-xs">
          Conçu avec ❤️ grâce à{' '}
          <a 
            href="https://claude.ai/code" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 transition"
          >
            Claude Code
          </a>
        </p>
      </footer>
    </div>
  );
}
